import { Button, IconFont, usePopoverHeight } from '@byecode/ui'
import type { AvailableFieldBlockInfo, AvailableFilterBlockInfo, FilterFormType, ViewType } from '@lighthouse/core'
import { FilterMode } from '@lighthouse/core'
import type { FlowNode } from '@lighthouse/shared'
import {
    getAppointField,
    getIsFindUseInFilter,
    getViewColumns,
    notDataSettingInnerTypes,
    useAtomAction,
    useAtomData,
    useFindUseObjectContext,
    ViewFieldFilterConfigure
} from '@lighthouse/shared'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { getDataSourceMetaAtom } from '@/atoms/dataSource/action'
import {
    blockHighlightAtom,
    lastPageOfStackAtom,
    pagePathParameterFamily,
    pageQueryParameterFamily,
    pageStatesParameterFamily,
    pageVariableSchemasFamily
} from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'

import { PopoverButton } from '../PopoverButton'

const SCxContent = styled.div`
    padding: 0 16px;
`

const SCxButton = styled(Button)`
    margin: 8px 0;
`

const SCxDescription = styled.div`
    margin: 4px 0;
    color: var(--color-gray-500);
    line-height: 20px;
    font-size: var(--font-size-sm);
`

interface TriggerFilterProps {
    // allParentNodes: FlowNode[]
    value: FilterFormType
    sourceFieldFlowNode?: FlowNode[]
    dataSourceId?: string
    paramsMode?: FilterMode
    fieldMode?: FilterMode
    description?: string
    prefix: string
    filterName?: string
    formDsId?: string
    viewType?: ViewType
    viewDsId?: string
    availableFieldBlocksInfo?: AvailableFieldBlockInfo[]
    availableFilterBlocksInfo?: AvailableFilterBlockInfo[]
    style?: React.CSSProperties
    onChange: (value: FilterFormType | undefined) => void
}

export const TriggerFilter: React.FC<TriggerFilterProps> = ({
    value,
    sourceFieldFlowNode,
    dataSourceId = '',
    fieldMode = FilterMode.NORMAL,
    paramsMode = FilterMode.NORMAL,
    filterName = 'filter',
    prefix,
    style,
    viewType,
    viewDsId,
    availableFieldBlocksInfo,
    availableFilterBlocksInfo,
    formDsId,
    description,
    onChange
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const dataSource = useDataSource(appId, envId, dataSourceId)
    const { run: getDataSourceMeta } = useAtomAction(getDataSourceMetaAtom)
    // const { control } = useFormContext()
    const findUseObject = useFindUseObjectContext()

    const disabledWithVersion = useIsDisabledWithVersion()

    const { type } = useFlowContainerContext()

    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => (type ? '' : s?.pageId || ''), [type])
    )
    const pageVariables = useAtomData(pageVariableSchemasFamily(pageId))
    const pagePathParameter = useAtomData(pagePathParameterFamily(pageId))
    const pageQueryParameter = useAtomData(pageQueryParameterFamily(pageId))
    const pageStateParameter = useAtomData(pageStatesParameterFamily(pageId))
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)

    const handleMouseEnter = useCallback(
        (id: string) => {
            setBlockHighlight([id])
            document.querySelector(`div[data-node-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
        [setBlockHighlight]
    )

    const handleMouseLeave = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])
    const [open, setOpen] = useState(false)

    useEffect(() => {
        if (dataSourceId) {
            getDataSourceMeta({ envId, dsId: dataSourceId })
        }
    }, [appId, dataSourceId, envId, getDataSourceMeta])

    const columns = useMemo(
        () =>
            getViewColumns({
                tableProps: dataSource?.viewOptions?.tableProps || [],
                schema: dataSource?.schema || {}
            }),
        [dataSource?.schema, dataSource?.viewOptions?.tableProps]
    )

    const primaryField = useMemo(() => {
        if (!dataSource) {
            return columns[0]?.fieldId
        }
        const field = getAppointField(dataSource, 'ID')
        return field?.id || columns[0]?.fieldId
    }, [columns, dataSource])

    const { height, ref } = usePopoverHeight<HTMLButtonElement>({ opened: open, deps: [value] })

    const filtersLength = useMemo(() => {
        return value?.expression?.conditions?.length || 0
    }, [value?.expression?.conditions?.length])

    const isHighLight = useMemo(() => {
        return getIsFindUseInFilter({
            data: value,
            findUseObject,
            currentDsId: dataSourceId,
            allParentNodes: sourceFieldFlowNode
        })
    }, [dataSourceId, findUseObject, sourceFieldFlowNode, value])

    return (
        <SCxContent style={style}>
            {description && <SCxDescription>{description}</SCxDescription>}

            <PopoverButton
                withinPortal
                width="auto"
                trapFocus={false}
                opened={open}
                onChange={setOpen}
                positionDependencies={[value]}
                popupContent={
                    <ViewFieldFilterConfigure
                        maxHeight={height}
                        dataSource={dataSource}
                        dataSourceList={dataSourceList}
                        workflowList={workflowList}
                        availableFieldBlocksInfo={availableFieldBlocksInfo}
                        availableFilterBlocksInfo={availableFilterBlocksInfo}
                        pagePathParameter={pagePathParameter}
                        pageQueryParameter={pageQueryParameter}
                        pageVariables={pageVariables}
                        pageStateParameter={pageStateParameter}
                        upstreamNodes={type && sourceFieldFlowNode}
                        allUpstreamNodes={type && sourceFieldFlowNode}
                        formDsId={formDsId}
                        viewType={viewType}
                        viewDsId={viewDsId}
                        paramsMode={paramsMode}
                        fieldMode={fieldMode}
                        columns={columns}
                        primaryField={primaryField}
                        filter={value}
                        disabled={disabledWithVersion}
                        noSettingInnerType={notDataSettingInnerTypes}
                        onFilter={onChange}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    />
                }
            >
                <SCxButton
                    ref={ref}
                    highlighting={isHighLight}
                    icon={<IconFont type="Setting" size={16} color="var(--color-gray-400)" />}
                    block
                >
                    {filtersLength > 0 ? `已设 ${filtersLength} 个条件` : '设置条件'}
                </SCxButton>
            </PopoverButton>
        </SCxContent>
    )
}
